import { z } from 'zod';
import { uuidSchema, isoDateTimeSchema, emailSchema, phoneSchema } from '../schemas/common';

// Channels + kinds match the Postgres enums in 0006_sprint3_messaging.sql.
export const notificationChannelSchema = z.enum(['email', 'sms', 'push', 'in_app']);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

export const notificationKindSchema = z.enum([
  'message',
  'broadcast',
  'schedule_change',
  'payment_due',
  'payment_failed',
  'costume_due',
  'comp_update',
  'enrollment',
]);
export type NotificationKind = z.infer<typeof notificationKindSchema>;

export const deliveryStatusSchema = z.enum(['pending', 'sent', 'failed', 'rejected', 'bounced']);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

export const broadcastScopeSchema = z.enum(['all', 'class', 'comp']);
export type BroadcastScope = z.infer<typeof broadcastScopeSchema>;

// Preferences ---------------------------------------------------------------

export const notificationPreferenceSchema = z.object({
  user_id: uuidSchema,
  kind: notificationKindSchema,
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;

// Messaging -----------------------------------------------------------------

export const messageThreadSchema = z.object({
  id: uuidSchema,
  family_id: uuidSchema,
  subject: z.string().min(1).max(200),
  archived: z.boolean(),
  created_at: isoDateTimeSchema,
  updated_at: isoDateTimeSchema,
  last_message_at: isoDateTimeSchema,
});
export type MessageThread = z.infer<typeof messageThreadSchema>;

export const messageInputSchema = z.object({
  thread_id: uuidSchema,
  body: z.string().trim().min(1).max(4000),
});
export type MessageInput = z.infer<typeof messageInputSchema>;

export const messageSchema = z.object({
  id: uuidSchema,
  thread_id: uuidSchema,
  author_user_id: uuidSchema,
  author_role: z.enum(['admin', 'faculty', 'parent', 'dancer']),
  body: z.string(),
  created_at: isoDateTimeSchema,
  read_by_recipient_at: isoDateTimeSchema.nullable(),
});
export type Message = z.infer<typeof messageSchema>;

// Broadcasts ----------------------------------------------------------------

export const broadcastInputSchema = z
  .object({
    scope: broadcastScopeSchema,
    scope_target_id: uuidSchema.nullable().optional(),
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(4000),
    channels: z.array(notificationChannelSchema).min(1),
    scheduled_for: isoDateTimeSchema.nullable().optional(),
  })
  .refine((b) => (b.scope === 'class' ? !!b.scope_target_id : true), {
    message: 'A class-scoped broadcast must include the class id',
    path: ['scope_target_id'],
  })
  .refine((b) => (b.scope !== 'class' ? !b.scope_target_id : true), {
    message: 'scope_target_id is only valid for class-scoped broadcasts',
    path: ['scope_target_id'],
  });
export type BroadcastInput = z.infer<typeof broadcastInputSchema>;

// Push subscription ---------------------------------------------------------

export const pushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  // RFC 8291 keys are url-safe base64. Don't bother with strict regex —
  // browsers produce them; we just store them.
  p256dh: z.string().min(10),
  auth: z.string().min(10),
  user_agent: z.string().optional(),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

// Recipients ----------------------------------------------------------------
// Used by the notify orchestrator (Edge Function side) to fan out a single
// event to multiple users on multiple channels.

export interface NotificationRecipient {
  user_id: string;
  role: 'admin' | 'faculty' | 'parent' | 'dancer';
  email: string | null;
  phone: string | null;
  // Preferences keyed by `${kind}:${channel}` — absent key means "use default".
  preferences: Record<string, boolean>;
}

export interface NotificationPayload {
  kind: NotificationKind;
  title: string;
  body: string;
  // Optional URL the in-app / push notification deep-links to.
  link?: string;
  // Free-form metadata (kept separate from body so the Edge Function can
  // template per channel — short SMS, full email, etc.).
  meta?: Record<string, unknown>;
}

export { emailSchema, phoneSchema };
